import React, { useCallback, useEffect, useState } from "react";
import { MapPinned, Plus, Pencil, Trash2, Star, Route, ShieldCheck } from "lucide-react";
import {
  PageHeader, Button, Input, Textarea, Card, CardContent, StatusBadge, LoadingBlock, EmptyState,
  Modal, ConfirmDialog, toast,
} from "@/components/ds";
import { api, formatApiError, type LocationConfig, type BranchLocation, type RouteResult } from "@/lib/api";

const EMPTY = { name: "", market: "", phone_number: "", zip_codes: "", service_types: "", priority: "100", greeting: "", business_open_hour: "", business_close_hour: "", active: true, is_default: false };

export default function Locations() {
  const [config, setConfig] = useState<LocationConfig | null>(null);
  const [locs, setLocs] = useState<BranchLocation[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BranchLocation | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState<BranchLocation | null>(null);

  const load = useCallback(() => {
    api.locList().then(setLocs).catch((e) => toast.error(formatApiError(e)));
    api.locConfig().then(setConfig).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setShowForm(true); };
  const openEdit = (l: BranchLocation) => {
    setEditing(l);
    setForm({ name: l.name, market: l.market ?? "", phone_number: l.phone_number ?? "", zip_codes: (l.zip_codes || []).join(", "),
      service_types: (l.service_types || []).join(", "), priority: String(l.priority), greeting: l.greeting ?? "",
      business_open_hour: l.business_open_hour != null ? String(l.business_open_hour) : "",
      business_close_hour: l.business_close_hour != null ? String(l.business_close_hour) : "", active: l.active, is_default: l.is_default });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    const body = {
      name: form.name.trim(), market: form.market.trim() || null, phone_number: form.phone_number.trim() || null,
      zip_codes: form.zip_codes.split(",").map((s) => s.trim()).filter(Boolean),
      service_types: form.service_types.split(",").map((s) => s.trim()).filter(Boolean),
      priority: Number(form.priority) || 100, greeting: form.greeting.trim() || null,
      business_open_hour: form.business_open_hour === "" ? null : Number(form.business_open_hour),
      business_close_hour: form.business_close_hour === "" ? null : Number(form.business_close_hour),
      active: form.active, is_default: form.is_default,
    };
    try {
      if (editing) await api.locUpdate(editing.id, body); else await api.locCreate(body);
      toast.success(editing ? "Location updated" : "Location added"); setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!del) return;
    try { await api.locDelete(del.id); toast.success("Location removed"); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setDel(null); }
  };

  const atLimit = config ? (locs?.length ?? 0) >= config.max_locations : false;

  return (
    <div className="space-y-6" data-testid="page-locations">
      <PageHeader title="Locations & Routing" description="Define branches, service areas and phone numbers. Calls are routed to the right location automatically." testid="locations-header"
        action={<Button onClick={openCreate} disabled={atLimit} data-testid="location-new-btn"><Plus className="h-4 w-4" /> Add location</Button>} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="locations-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          <StatusBadge tone="slate">{config.location_count} / {config.max_locations} locations</StatusBadge>
          {config.routing_enabled ? <StatusBadge tone="green">Smart routing</StatusBadge> : <StatusBadge tone="amber">Single-location routing</StatusBadge>}
          {config.markets_enabled && <StatusBadge tone="green">Multi-market</StatusBadge>}
        </div>
      )}

      {config && !config.routing_enabled && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="locations-upsell">
          <Route className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <p className="text-xs text-blue-800">Your plan routes everything to one location. Upgrade to <b>Pro</b> for ZIP/service-based routing across up to 5 branches, or <b>Custom</b> for advanced multi-market routing with priority chains and per-branch receptionist configs.</p>
        </div>
      )}

      {locs === null ? <LoadingBlock rows={3} /> : locs.length === 0 ? (
        <EmptyState icon={MapPinned} title="No locations yet" description="Add your first branch with its phone number and service area." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="locations-list">
          {locs.map((l) => (
            <Card key={l.id}><CardContent className="space-y-2 p-4" data-testid={`location-${l.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{l.name}</h3>
                    {l.is_default && <StatusBadge tone="blue"><Star className="mr-1 inline h-3 w-3" />Default</StatusBadge>}
                    {l.market && <StatusBadge tone="slate">{l.market}</StatusBadge>}
                    {!l.active && <StatusBadge tone="amber">Inactive</StatusBadge>}
                  </div>
                  {l.phone_number && <p className="text-xs text-slate-500">{l.phone_number}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`location-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setDel(l)} data-testid={`location-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              {l.zip_codes.length > 0 && <p className="text-xs text-slate-500">Service area: {l.zip_codes.join(", ")}</p>}
              {l.service_types.length > 0 && <p className="text-xs text-slate-500">Handles: {l.service_types.join(", ")}</p>}
              <p className="text-[11px] text-slate-400">Priority {l.priority}{l.business_open_hour != null ? ` · open ${l.business_open_hour}:00–${l.business_close_hour}:00` : ""}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      {config?.routing_enabled && <RouteTester />}

      <Modal open={showForm} onOpenChange={setShowForm} title={editing ? "Edit location" : "Add location"} testid="location-form-modal">
        <div className="max-h-[74vh] space-y-3 overflow-y-auto pr-1">
          <div><p className="mb-1 text-xs font-medium text-slate-500">Branch name</p><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="location-name" /></div>
          {config?.markets_enabled && <div><p className="mb-1 text-xs font-medium text-slate-500">Market (Custom)</p><Input value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} placeholder="e.g. North Texas" data-testid="location-market" /></div>}
          <div><p className="mb-1 text-xs font-medium text-slate-500">Phone number</p><Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+1..." data-testid="location-phone" /></div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Service-area ZIPs (comma-separated)</p><Input value={form.zip_codes} onChange={(e) => setForm({ ...form, zip_codes: e.target.value })} placeholder="75001, 75002" data-testid="location-zips" /></div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Service types handled (comma-separated)</p><Input value={form.service_types} onChange={(e) => setForm({ ...form, service_types: e.target.value })} placeholder="AC repair, Plumbing" data-testid="location-services" /></div>
          <div className="flex gap-3">
            <div className="w-28"><p className="mb-1 text-xs font-medium text-slate-500">Priority</p><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="location-priority" /></div>
            <div className="w-28"><p className="mb-1 text-xs font-medium text-slate-500">Open hour</p><Input type="number" min="0" max="23" value={form.business_open_hour} onChange={(e) => setForm({ ...form, business_open_hour: e.target.value })} data-testid="location-open" /></div>
            <div className="w-28"><p className="mb-1 text-xs font-medium text-slate-500">Close hour</p><Input type="number" min="0" max="24" value={form.business_close_hour} onChange={(e) => setForm({ ...form, business_close_hour: e.target.value })} data-testid="location-close" /></div>
          </div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Receptionist greeting (optional)</p><Textarea value={form.greeting} onChange={(e) => setForm({ ...form, greeting: e.target.value })} rows={2} data-testid="location-greeting" /></div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="location-active" /> Active</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="location-default" /> Default branch</label>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="location-save-btn">{editing ? "Save" : "Add location"}</Button>
        </div>
      </Modal>

      <ConfirmDialog open={!!del} onOpenChange={(o) => { if (!o) setDel(null); }} onConfirm={doDelete} title="Remove location?" description="This branch will no longer receive routed calls." confirmLabel="Remove" destructive testid="location-confirm-delete" />
    </div>
  );
}

function RouteTester() {
  const [zip, setZip] = useState("");
  const [service, setService] = useState("");
  const [dialed, setDialed] = useState("");
  const [res, setRes] = useState<RouteResult | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try { setRes(await api.locRoute({ zip: zip || undefined, service: service || undefined, phone_dialed: dialed || undefined })); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Card><CardContent className="space-y-3 p-4" data-testid="route-tester">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Route className="h-4 w-4 text-blue-600" /> Test routing</h3>
      <p className="text-xs text-slate-500">Routing is decided server-side from these signals — location IDs from the browser are never trusted.</p>
      <div className="flex flex-wrap gap-2">
        <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="Caller ZIP" className="w-40" data-testid="route-zip" />
        <Input value={service} onChange={(e) => setService(e.target.value)} placeholder="Requested service" className="w-48" data-testid="route-service" />
        <Input value={dialed} onChange={(e) => setDialed(e.target.value)} placeholder="Dialed number" className="w-44" data-testid="route-dialed" />
        <Button onClick={run} disabled={busy} data-testid="route-run-btn">Resolve</Button>
      </div>
      {res && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="route-result">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800"><ShieldCheck className="h-4 w-4" /> Routed to: {res.location.name}</p>
          <p className="mt-1 text-xs text-emerald-700">{res.reasons.join(" · ")}</p>
          <p className="mt-1 text-[11px] text-slate-500">{res.candidates} active location(s) considered</p>
        </div>
      )}
    </CardContent></Card>
  );
}
